import { AxiosResponse } from "axios";
import { normalize, schema } from 'normalizr';
import { createAsyncThunk } from "@reduxjs/toolkit";
import mineCraftApi from 'src/api/mineCraftApi';
import minecraftUrls from "src/configs/mine_craft/minecraftUrls";
import { ServerInterface } from 'src/store/mine_craft/serversSlice';

const processStrategy = (value: any) => {
  return {
    ...value,
    url: minecraftUrls.serverPath(value.id),
    image_url: minecraftUrls.remoteUrl(value.image_url)
  };
}

export const serversEntity = new schema.Entity(
  'servers',
  {},
  {
    processStrategy
  }
);

export const serverEntity = new schema.Entity(
  'server',
  {},
  {
    processStrategy
  }
);

const fetchServersCollection = async (url: string, params: object = {}) => {
  const response    = await mineCraftApi.get(url, { params })
  const normalized  = normalize<
    any,
    {
      servers: { [key: string]: ServerInterface };
    }
  >(response.data.servers, [serversEntity]);

  return {
    servers: normalized.entities?.servers || {},
    ids: normalized.result,
    count: response.data.meta?.total_count
  };
}

const normalizeInstance = (response: AxiosResponse) => {
  const normalized = normalize<
    any,
    {
      server: ServerInterface;
    }
  >(response.data.server, serverEntity);
  return normalized.entities;
}

export const fetchServers = createAsyncThunk(
  'servers/fetchServers',
  async (params: object = {}) => {
    const url = params['state'] ? `/onesky/servers/${params['state']}` : '/onesky/servers';

    return fetchServersCollection(url, params);
  }
);

export const fetchServer = createAsyncThunk(
  'servers/fetchServer',
  async (id: number | string) => {
    const response = await mineCraftApi.get(`/onesky/servers/${id}`);
    return normalizeInstance(response);
  }
);

export const createServer = createAsyncThunk(
  'servers/createServer',
  async (data: object, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.post(`/onesky/servers`, data);
      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const updateServer = createAsyncThunk(
  'servers/updateServer',
  async ({ id, data }: { id: number, data: object }, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.patch(`/onesky/servers/${id}`, data);
      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error.response.data || error)
    }
  }
);

// Hosted (crafty-servers) config of an existing server — gamemode,
// access, power mode… Answers the refreshed server like the power
// actions do, so the slice swaps the entity in place.
export const updateServerHostedConfig = createAsyncThunk(
  'servers/updateServerHostedConfig',
  async ({ id, data }: { id: number, data: object }, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.patch(`/onesky/servers/${id}/hosted_config`, { hosted_config: data });
      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const destroyServer = createAsyncThunk(
  'servers/destroyServer',
  async (id: number) => {
    const response = await mineCraftApi.delete(`/onesky/servers/${id}`);
    return response.data;
  }
);

// Hosted (crafty-servers) lifecycle — every action returns the
// refreshed server (with its nested hosted_server), so the slice just
// swaps the entity in place.
export const startServer = createAsyncThunk(
  'servers/startServer',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.post(`/onesky/servers/${id}/start`);
      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const stopServer = createAsyncThunk(
  'servers/stopServer',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.post(`/onesky/servers/${id}/stop`);
      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const restartServer = createAsyncThunk(
  'servers/restartServer',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.post(`/onesky/servers/${id}/restart`);
      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

// Stop with wake-on-connect: the waker keeps the port and starts the
// server again on the first player join.
export const sleepServer = createAsyncThunk(
  'servers/sleepServer',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.post(`/onesky/servers/${id}/sleep`);
      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const syncServer = createAsyncThunk(
  'servers/syncServer',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.post(`/onesky/servers/${id}/sync`);
      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

// Console command + live stats: both answer raw crafty-servers payloads
// ({ sent } / { state, cpuPercent, ... }), so nothing lands in the
// slice — callers unwrap the fulfilled payload directly.
export const commandServer = createAsyncThunk(
  'servers/commandServer',
  async ({ id, command }: { id: number, command: string }, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.post(`/onesky/servers/${id}/command`, { command });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const fetchServerStats = createAsyncThunk(
  'servers/fetchServerStats',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.get(`/onesky/servers/${id}/stats`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

// Sampled CPU/RAM/disk/network history for the usage charts —
// { interval, samples: [{ ts, cpuPercent, memoryBytes, diskBytes,
// networkRxBytes, networkTxBytes }] }. `minute` covers the last 6h,
// `hour` the last 7 days.
export const fetchServerStatsHistory = createAsyncThunk(
  'servers/fetchServerStatsHistory',
  async ({ id, interval }: { id: number, interval?: string }, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.get(`/onesky/servers/${id}/stats/history`, { params: { interval } });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const fetchServerPlayers = createAsyncThunk(
  'servers/fetchServerPlayers',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.get(`/onesky/servers/${id}/players`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

// Lifetime aggregates + the live count, with the configured cap folded in
// server-side. Reads stored history rather than Wings, so it answers on a
// stopped server too — unlike /stats and /players.
export const fetchServerPlayerStats = createAsyncThunk(
  'servers/fetchServerPlayerStats',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.get(`/onesky/servers/${id}/players/stats`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

// Console tail — { lines: [{ ts, line }] } from crafty-servers' in-memory
// buffer (max 500 lines), so it answers on a stopped server too.
export const fetchServerLogs = createAsyncThunk(
  'servers/fetchServerLogs',
  async ({ id, limit }: { id: number, limit?: number }, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.get(`/onesky/servers/${id}/logs`, { params: { limit } });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

// Backups live only on crafty-servers, so these all answer its payloads
// straight through — nothing lands in the slice.
export const fetchServerBackups = createAsyncThunk(
  'servers/fetchServerBackups',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.get(`/onesky/servers/${id}/backups`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const createServerBackup = createAsyncThunk(
  'servers/createServerBackup',
  async ({ id, name }: { id: number, name?: string }, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.post(`/onesky/servers/${id}/backups`, { name });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

// Needs a stopped server (409 otherwise); `truncate` wipes the current
// files before restoring.
export const restoreServerBackup = createAsyncThunk(
  'servers/restoreServerBackup',
  async (
    { id, backupId, truncate }: { id: number, backupId: string, truncate?: boolean },
    { rejectWithValue }
  ) => {
    try {
      const response = await mineCraftApi.post(`/onesky/servers/${id}/backups/${backupId}/restore`, { truncate });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const destroyServerBackup = createAsyncThunk(
  'servers/destroyServerBackup',
  async ({ id, backupId }: { id: number, backupId: string }, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.delete(`/onesky/servers/${id}/backups/${backupId}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

// The signed URL expires in minutes — fetch it on the click, never upfront.
export const fetchServerBackupDownload = createAsyncThunk(
  'servers/fetchServerBackupDownload',
  async ({ id, backupId }: { id: number, backupId: string }, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.get(`/onesky/servers/${id}/backups/${backupId}/download`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

// Member rows carry the linked account's avatar as a storage path, like
// every other user payload — see usersActions' processStrategy.
const processMember = (member: any) => ({
  ...member,
  user_avatar_url: minecraftUrls.remoteUrl(member.user_avatar_url)
});

// Roster / roles for private servers. crafty-servers has no user model —
// roles live in the minecraft DB and are translated into its allowlist +
// kick primitives server-side.
export const fetchServerMembers = createAsyncThunk(
  'servers/fetchServerMembers',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.get(`/onesky/servers/${id}/members`);
      return { ...response.data, members: (response.data.members || []).map(processMember) };
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const inviteServerMember = createAsyncThunk(
  'servers/inviteServerMember',
  async ({ id, data }: { id: number, data: object }, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.post(`/onesky/servers/${id}/members`, data);
      return { ...response.data, member: processMember(response.data.member || {}) };
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

// Role and/or gamertag. Setting the gamertag is what moves a pending
// member onto the server's allowlist, so it is a write with real effect.
export const updateServerMember = createAsyncThunk(
  'servers/updateServerMember',
  async (
    { id, memberId, data }: { id: number, memberId: number, data: { role?: string, gamertag?: string } },
    { rejectWithValue }
  ) => {
    try {
      const response = await mineCraftApi.patch(`/onesky/servers/${id}/members/${memberId}`, data);
      return { ...response.data, member: processMember(response.data.member || {}) };
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const removeServerMember = createAsyncThunk(
  'servers/removeServerMember',
  async ({ id, memberId }: { id: number, memberId: number }, { rejectWithValue }) => {
    try {
      await mineCraftApi.delete(`/onesky/servers/${id}/members/${memberId}`);
      return { memberId };
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const kickServerPlayer = createAsyncThunk(
  'servers/kickServerPlayer',
  async ({ id, gamertag, reason }: { id: number, gamertag: string, reason?: string }, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.post(`/onesky/servers/${id}/kick`, { gamertag, reason });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

// Per-player moderation. Bans are kept by crafty-servers itself (vanilla
// Bedrock has no ban list) and enforced by auto-kick on every connect.
// Raw payloads — nothing lands in the slice.
export const fetchServerBans = createAsyncThunk(
  'servers/fetchServerBans',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.get(`/onesky/servers/${id}/bans`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const banServerPlayer = createAsyncThunk(
  'servers/banServerPlayer',
  async ({ id, gamertag, reason }: { id: number, gamertag: string, reason?: string }, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.post(`/onesky/servers/${id}/bans`, { gamertag, reason });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const unbanServerPlayer = createAsyncThunk(
  'servers/unbanServerPlayer',
  async ({ id, gamertag }: { id: number, gamertag: string }, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.delete(`/onesky/servers/${id}/bans/${encodeURIComponent(gamertag)}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

// op / deop / heal a connected player — transient console commands, so
// the server must be running and the player online (heal additionally
// needs cheats enabled on the server).
export const playerServerAction = createAsyncThunk(
  'servers/playerServerAction',
  async (
    { id, gamertag, action }: { id: number, gamertag: string, action: 'op' | 'deop' | 'heal' },
    { rejectWithValue }
  ) => {
    try {
      const response = await mineCraftApi.post(
        `/onesky/servers/${id}/players/${encodeURIComponent(gamertag)}/${action}`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

// Everything crafty knows about one player on this server: presence,
// sessions, playtime, ban and allowlist status.
export const fetchServerPlayerProfile = createAsyncThunk(
  'servers/fetchServerPlayerProfile',
  async ({ id, gamertag }: { id: number, gamertag: string }, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.get(
        `/onesky/servers/${id}/players/${encodeURIComponent(gamertag)}/profile`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

// Analytics: session history / activity timeline / uptime history /
// live ping. Read-only crafty payloads — nothing lands in the slice.
export const fetchServerSessions = createAsyncThunk(
  'servers/fetchServerSessions',
  async (
    { id, params }: { id: number, params?: { limit?: number, offset?: number, player?: string, from?: string, to?: string } },
    { rejectWithValue }
  ) => {
    try {
      const response = await mineCraftApi.get(`/onesky/servers/${id}/players/history`, { params });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const fetchServerPlayersTimeline = createAsyncThunk(
  'servers/fetchServerPlayersTimeline',
  async ({ id, interval }: { id: number, interval?: 'hour' | 'day' }, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.get(`/onesky/servers/${id}/players/timeline`, { params: { interval } });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const fetchServerStatusHistory = createAsyncThunk(
  'servers/fetchServerStatusHistory',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.get(`/onesky/servers/${id}/status/history`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

// A real RakNet ping on the game port — what a player would see from
// outside. `online: false` is a valid answer, not an error.
export const pingServer = createAsyncThunk(
  'servers/pingServer',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.get(`/onesky/servers/${id}/ping`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

// Sets one gamerule on the running server — applies immediately, no
// restart. The value keeps its JSON type (boolean/integer/word).
export const setServerGamerule = createAsyncThunk(
  'servers/setServerGamerule',
  async (
    { id, rule, value }: { id: number, rule: string, value: boolean | number | string },
    { rejectWithValue }
  ) => {
    try {
      const response = await mineCraftApi.post(`/onesky/servers/${id}/gamerules`, { rule, value });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

// Cron schedules (auto-restarts, timed commands, nightly backups).
// Attributes travel nested under `schedule:` — the wire field is
// literally named `action`, which flattened would collide with Rails'
// own params[:action]. Max 10 per server (429 past the cap).
export interface ScheduleAttributes {
  action?: 'RESTART' | 'STOP' | 'START' | 'COMMAND' | 'BACKUP';
  cron?: string;
  payload?: string;
  enabled?: boolean;
}

export const fetchServerSchedules = createAsyncThunk(
  'servers/fetchServerSchedules',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.get(`/onesky/servers/${id}/schedules`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const createServerSchedule = createAsyncThunk(
  'servers/createServerSchedule',
  async ({ id, data }: { id: number, data: ScheduleAttributes }, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.post(`/onesky/servers/${id}/schedules`, { schedule: data });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const updateServerSchedule = createAsyncThunk(
  'servers/updateServerSchedule',
  async (
    { id, scheduleId, data }: { id: number, scheduleId: string, data: ScheduleAttributes },
    { rejectWithValue }
  ) => {
    try {
      const response = await mineCraftApi.patch(`/onesky/servers/${id}/schedules/${scheduleId}`, { schedule: data });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const destroyServerSchedule = createAsyncThunk(
  'servers/destroyServerSchedule',
  async ({ id, scheduleId }: { id: number, scheduleId: string }, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.delete(`/onesky/servers/${id}/schedules/${scheduleId}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

// Worlds on the hosted server. Raw crafty payloads — nothing lands in
// the slice. The upload answers 202 with a tracked content job (same
// polling endpoints as a content install).
export const fetchServerWorlds = createAsyncThunk(
  'servers/fetchServerWorlds',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.get(`/onesky/servers/${id}/worlds`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const uploadServerWorld = createAsyncThunk(
  'servers/uploadServerWorld',
  async (
    { id, url, name, activate }: { id: number, url: string, name: string, activate?: boolean },
    { rejectWithValue }
  ) => {
    try {
      const response = await mineCraftApi.post(`/onesky/servers/${id}/worlds`, { url, name, activate });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const activateServerWorld = createAsyncThunk(
  'servers/activateServerWorld',
  async ({ id, world, restart }: { id: number, world: string, restart?: boolean }, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.post(
        `/onesky/servers/${id}/worlds/${encodeURIComponent(world)}/activate`, { restart }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const destroyServerWorld = createAsyncThunk(
  'servers/destroyServerWorld',
  async ({ id, world }: { id: number, world: string }, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.delete(`/onesky/servers/${id}/worlds/${encodeURIComponent(world)}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

// The signed URL expires in minutes — fetch it on the click, never upfront.
export const fetchServerWorldDownload = createAsyncThunk(
  'servers/fetchServerWorldDownload',
  async ({ id, world }: { id: number, world: string }, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.get(
        `/onesky/servers/${id}/worlds/${encodeURIComponent(world)}/download`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const fetchServerWorldSettings = createAsyncThunk(
  'servers/fetchServerWorldSettings',
  async ({ id, world }: { id: number, world: string }, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.get(
        `/onesky/servers/${id}/worlds/${encodeURIComponent(world)}/settings`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

// The active world only accepts edits while the server is stopped (409
// otherwise) — the server would overwrite the file on save.
export const updateServerWorldSettings = createAsyncThunk(
  'servers/updateServerWorldSettings',
  async (
    { id, world, settings }: { id: number, world: string, settings: { [key: string]: boolean | number | string } },
    { rejectWithValue }
  ) => {
    try {
      const response = await mineCraftApi.patch(
        `/onesky/servers/${id}/worlds/${encodeURIComponent(world)}/settings`, { settings }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

// Wipes the current world and regenerates it (optionally on a new seed).
// IRREVERSIBLE without a backup.
export const resetServerWorld = createAsyncThunk(
  'servers/resetServerWorld',
  async ({ id, seed }: { id: number, seed?: string }, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.post(`/onesky/servers/${id}/world/reset`, { seed });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

// Content installs (addons / worlds / texture packs): answers 202 with
// `content_jobs`, one per item. Raw payloads — callers unwrap the
// fulfilled result directly.
export const installServerContent = createAsyncThunk(
  'servers/installServerContent',
  async ({ id, data }: { id: number, data: object }, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.post(`/onesky/servers/${id}/content`, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const fetchServerContent = createAsyncThunk(
  'servers/fetchServerContent',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.get(`/onesky/servers/${id}/content`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

// Bedrock builds crafty-servers can install — { latest, latestPreview,
// default, stable[], preview[], fetchedAt }, stable newest first.
export const fetchServerVersions = createAsyncThunk(
  'servers/fetchServerVersions',
  async (_: void, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.get('/onesky/servers/versions');
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const fetchServerContentJobs = createAsyncThunk(
  'servers/fetchServerContentJobs',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.get(`/onesky/servers/${id}/content_jobs`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const syncServerContentJob = createAsyncThunk(
  'servers/syncServerContentJob',
  async ({ id, jobId }: { id: number, jobId: number }, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.post(`/onesky/servers/${id}/content_jobs/${jobId}/sync`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

// Enable / disable an installed pack in the current world (files kept),
// keyed by the pack's manifest uuid. Applies on the next restart unless
// `restart` rides along. Answers crafty's { pack, restarted }.
export const updateServerPack = createAsyncThunk(
  'servers/updateServerPack',
  async (
    { id, packId, active, restart }: { id: number, packId: string, active: boolean, restart: boolean },
    { rejectWithValue }
  ) => {
    try {
      const response = await mineCraftApi.patch(`/onesky/servers/${id}/content/${packId}`, { active, restart });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

// Delete an installed pack — world entry and files. Answers crafty's
// { deleted, restarted }. axios only sends a DELETE body through `data`.
export const destroyServerPack = createAsyncThunk(
  'servers/destroyServerPack',
  async ({ id, packId, restart }: { id: number, packId: string, restart: boolean }, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.delete(`/onesky/servers/${id}/content/${packId}`, { data: { restart } });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);
